import { courseServiceApi } from "../../apis/courseService.api";
import { COURSE_API_TAGS } from "../../app.constants";
import type {
  IPublicCourseResponse,
  LectureDetailsResponse,
  LectureDto,
  LectureResponse,
  QuestionDto,
  QuestionResponse,
  QuizDto,
  QuizOptionDto,
  QuizOptionResponse,
  QuizResponse,
  SectionDto,
  SectionResponse,
  UpdateLectureDto,
  UpdateQuestionDto,
  UpdateQuizDto,
  UpdateQuizOptionDto,
  UpdateSectionDto,
} from "./adminContent.types";

const ADMIN_URL = "/admin";

export const adminContentApi = courseServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    // CREATE SECTION
    createSection: builder.mutation<SectionResponse, SectionDto>({
      query: (body) => ({
        url: `${ADMIN_URL}/content/sections`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
        { type: COURSE_API_TAGS.SECTIONS, id: "LIST" },
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
      ],
    }),

    // GET SECTIONS BY COURSE ID
    getSectionsByCourseId: builder.query<
      SectionResponse[],
      { courseId: number }
    >({
      query: ({ courseId }) => ({
        url: `${ADMIN_URL}/content/sections/${courseId}`,
        method: "GET",
      }),
      providesTags: [{ type: COURSE_API_TAGS.SECTIONS, id: "LIST" }],
    }),

    // GET SECTION BY ID
    getSection: builder.query<SectionResponse, number>({
      query: (id) => ({
        url: `${ADMIN_URL}/content/sections/${id}`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: COURSE_API_TAGS.SECTIONS, id }],
    }),

    // UPDATE SECTION
    updateSection: builder.mutation<
      SectionResponse,
      { id: number; body: UpdateSectionDto }
    >({
      query: ({ id, body }) => ({
        url: `${ADMIN_URL}/content/sections/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
        { type: COURSE_API_TAGS.SECTIONS, id },
        { type: COURSE_API_TAGS.SECTIONS, id: "LIST" },
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
      ],
    }),

    // DELETE SECTION
    deleteSection: builder.mutation<void, number>({
      query: (id) => ({
        url: `${ADMIN_URL}/content/sections/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
        { type: COURSE_API_TAGS.SECTIONS, id },
        { type: COURSE_API_TAGS.SECTIONS, id: "LIST" },
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
      ],
    }),

    // CREATE LECTURE
    createLecture: builder.mutation<LectureResponse, LectureDto>({
      query: (body) => ({
        url: `${ADMIN_URL}/content/lectures`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
        { type: COURSE_API_TAGS.LECTURES, id: "LIST" },
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
      ],
    }),

    // UPDATE LECTURE
    updateLecture: builder.mutation<
      LectureResponse,
      { id: number; body: UpdateLectureDto }
    >({
      query: ({ id, body }) => ({
        url: `${ADMIN_URL}/content/lectures/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
        { type: COURSE_API_TAGS.LECTURES, id },
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
      ],
    }),

    // DELETE LECTURE
    deleteLecture: builder.mutation<void, number>({
      query: (id) => ({
        url: `${ADMIN_URL}/content/lectures/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
        { type: COURSE_API_TAGS.LECTURES, id },
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
      ],
    }),

    // GET Lecture BY ID
    getLectureById: builder.query<LectureDetailsResponse, number>({
      query: (id) => ({
        url: `${ADMIN_URL}/lectures/${id}`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: COURSE_API_TAGS.LECTURES, id }],
    }),

    // CREATE QUIZ
    createQuiz: builder.mutation<QuizResponse, QuizDto>({
      query: (body) => ({
        url: `${ADMIN_URL}/content/quizzes`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
        { type: COURSE_API_TAGS.QUIZZES, id: "LIST" },
      ],
    }),

    getLectureDetails: builder.query<QuizResponse, number>({
      query: (id) => ({
        url: `/v1/lectures/${id}/content`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: COURSE_API_TAGS.LECTURES, id }],
    }),

    // GET QUIZ BY ID
    getQuiz: builder.query<QuizResponse, number>({
      query: (id) => ({
        url: `${ADMIN_URL}/content/quizzes/${id}`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: COURSE_API_TAGS.QUIZZES, id }],
    }),

    // UPDATE QUIZ
    updateQuiz: builder.mutation<
      QuizResponse,
      { id: number; body: UpdateQuizDto }
    >({
      query: ({ id, body }) => ({
        url: `${ADMIN_URL}/content/quizzes/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
        { type: COURSE_API_TAGS.QUIZZES, id },
      ],
    }),

    // DELETE QUIZ
    deleteQuiz: builder.mutation<void, number>({
      query: (id) => ({
        url: `${ADMIN_URL}/content/quizzes/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
        { type: COURSE_API_TAGS.QUIZZES, id },
      ],
    }),

    // CREATE QUESTION
    createQuestion: builder.mutation<QuestionResponse, QuestionDto>({
      query: (body) => ({
        url: `${ADMIN_URL}/content/questions`,
        method: "POST",
        body,
      }),
      // Correct invalidatesTags format
      invalidatesTags: [
        { type: COURSE_API_TAGS.QUESTIONS },
        { type: COURSE_API_TAGS.QUIZZES },
      ],
    }),
    // GET QUESTION BY ID
    getQuestion: builder.query<QuestionResponse, number>({
      query: (id) => ({
        url: `${ADMIN_URL}/content/questions/${id}`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: COURSE_API_TAGS.QUESTIONS, id }],
    }),

    // UPDATE QUESTION
    updateQuestion: builder.mutation<
      QuestionResponse,
      { id: number; body: UpdateQuestionDto }
    >({
      query: ({ id, body }) => ({
        url: `${ADMIN_URL}/content/questions/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: COURSE_API_TAGS.QUESTIONS, id },
        { type: COURSE_API_TAGS.QUIZZES },
      ],
    }),

    // DELETE QUESTION
    deleteQuestion: builder.mutation<void, number>({
      query: (id) => ({
        url: `${ADMIN_URL}/content/questions/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: COURSE_API_TAGS.QUESTIONS },
        { type: COURSE_API_TAGS.QUIZZES },
      ],
    }),

    // CREATE OPTION
    createOption: builder.mutation<QuizOptionResponse, QuizOptionDto>({
      query: (body) => ({
        url: `${ADMIN_URL}/content/options`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: COURSE_API_TAGS.OPTIONS },
        { type: COURSE_API_TAGS.QUESTIONS },
        { type: COURSE_API_TAGS.QUIZZES },
      ],
    }),

    // UPDATE OPTION
    updateOption: builder.mutation<
      QuizOptionResponse,
      { id: number; body: UpdateQuizOptionDto }
    >({
      query: ({ id, body }) => ({
        url: `${ADMIN_URL}/content/options/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: COURSE_API_TAGS.OPTIONS, id },
        { type: COURSE_API_TAGS.QUESTIONS },
        { type: COURSE_API_TAGS.QUIZZES },
      ],
    }),

    // DELETE OPTION
    deleteOption: builder.mutation<void, number>({
      query: (id) => ({
        url: `${ADMIN_URL}/content/options/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: COURSE_API_TAGS.OPTIONS },
        { type: COURSE_API_TAGS.QUESTIONS },
        { type: COURSE_API_TAGS.QUIZZES },
      ],
    }),

    // RECOMPUTE QUIZ MARKS
    recomputeQuizMarks: builder.mutation<void, number>({
      query: (quizId) => ({
        url: `${ADMIN_URL}/content/quizzes/${quizId}/recompute-marks`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, quizId) => [
        { type: COURSE_API_TAGS.QUIZZES, id: quizId },
      ],
    }),
    // Public Course Details
    publicCourseDetails: builder.query<IPublicCourseResponse, number>({
      query: (courseId) => ({
        url: `/public/courses/${courseId}`,
        method: "GET",
      }),
      providesTags: () => [{ type: COURSE_API_TAGS.COURSES, id: `Details` }],
    }),
  }),
});

export const {
  // Sections
  useCreateSectionMutation,
  useGetSectionsByCourseIdQuery,
  useGetSectionQuery,
  useUpdateSectionMutation,
  useDeleteSectionMutation,
  //   Lectures
  useCreateLectureMutation,
  useUpdateLectureMutation,
  useDeleteLectureMutation,
  useGetLectureDetailsQuery,
  useGetLectureByIdQuery,
  // Quizzes
  useCreateQuizMutation,
  useGetQuizQuery,
  useUpdateQuizMutation,
  useDeleteQuizMutation,
  //   Questions
  useCreateQuestionMutation,
  useGetQuestionQuery,
  useUpdateQuestionMutation,
  useDeleteQuestionMutation,
  //   Options
  useCreateOptionMutation,
  useUpdateOptionMutation,
  useDeleteOptionMutation,
  useRecomputeQuizMarksMutation,

  usePublicCourseDetailsQuery,
} = adminContentApi;
